import { syncDataLoaderFeature } from "@headless-tree/core"
import { useTree } from "@headless-tree/react"
import { type Document, getDocumentTrees, type Stored, UNTITLED_DOCUMENT_TITLE, uuidObj } from "@overmap-ai/common"
import { useMemo } from "react"

import { modelIconNameMapping } from "../../../mappings/icons"
import { Colors } from "../../../utils/colors"

export type DocumentNode = Stored<Document> & {
	children: Document["uuid"][]
}

export const useDocumentTree = (documents: Stored<Document>[]) => {
	const documentTrees = useMemo(() => {
		return getDocumentTrees(documents)
	}, [documents])

	const { rootItem, itemMapping } = useMemo(() => {
		const itemMapping: Record<DocumentNode["uuid"], DocumentNode> = {}

		for (const node of documentTrees.flatMap((tree) => tree.all()!)) {
			itemMapping[node.model.uuid] = {
				...node.model,
				children: node.children!.map(({ model }) => model.uuid),
			}
		}

		const rootItem: DocumentNode = uuidObj({
			submitted_at: new Date().toISOString(),
			title: "Root document",
			description: null,
			content: null,
			icon: modelIconNameMapping.document,
			color: Colors.gray,
			parent_document: null,
			children: documentTrees.map((tree) => tree.root!.model.uuid),
			project: null,
			organization: null,
			created_by: null,
		})

		itemMapping[rootItem.uuid] = rootItem

		return {
			itemMapping,
			rootItem,
		}
	}, [documentTrees])

	const tree = useTree<DocumentNode>({
		rootItemId: rootItem.uuid,
		getItemName: (item) => item.getItemData().title || UNTITLED_DOCUMENT_TITLE,
		isItemFolder: (item) => item.getItemData().children.length > 0,
		dataLoader: {
			getItem: (uuid) => itemMapping[uuid],
			getChildren: (uuid) => itemMapping[uuid].children,
		},
		features: [syncDataLoaderFeature],
	})

	return tree
}
